import { cn } from "@/lib/utils";
import { BASIS_STATUS_LABELS, INVOICE_STATUS_LABELS, STATUS_LABELS } from "@/lib/i18n";

/**
 * Status-chips: tekst + prikk, aldri farge åleine. Små, rolege, solide flater.
 * tone: neutral | info | warning | success | destructive | primary
 */
const TONE = {
  neutral: { chip: "bg-tint-neutral text-foreground", dot: "bg-brand-grey", text: "text-muted-foreground" },
  info: { chip: "bg-tint-info text-info", dot: "bg-info", text: "text-info" },
  warning: { chip: "bg-tint-warning text-warning", dot: "bg-warning", text: "text-warning" },
  success: { chip: "bg-tint-success text-success", dot: "bg-success", text: "text-success" },
  destructive: { chip: "bg-tint-destructive text-destructive", dot: "bg-destructive", text: "text-destructive" },
  primary: { chip: "bg-tint-primary text-primary", dot: "bg-primary", text: "text-primary" },
};

const ORDER_TONE = { ny: "info", i_arbeid: "warning", utfort: "success", avlyst: "neutral" };
const BASIS_TONE = { kladd: "neutral", godkjent: "success", erstatta: "neutral" };
const INVOICE_TONE = { ingen: "neutral", kladd: "neutral", godkjent: "info", eksportert: "info", fakturert: "success" };

export const toneFor = (kind, status) => {
  if (kind === "order") return ORDER_TONE[status] || "neutral";
  if (kind === "basis") return BASIS_TONE[status] || "neutral";
  if (kind === "invoice") return INVOICE_TONE[status] || "neutral";
  return "neutral";
};

/**
 * variant: "chip" (tint-flate) | "outline" (kantlinje) | "text" (berre prikk + tekst)
 * Ordre-status = chip. Faktura/revisjon = outline, så dei ikkje konkurrerer visuelt.
 */
export const StatusBadge = ({ status, kind = "order", size = "sm", variant, className, ...props }) => {
  let label = status;
  if (kind === "order") label = STATUS_LABELS[status] || status;
  else if (kind === "basis") label = BASIS_STATUS_LABELS[status] || status;
  else if (kind === "invoice") label = INVOICE_STATUS_LABELS[status] || status;
  const tone = TONE[toneFor(kind, status)];
  const v = variant || (kind === "order" ? "chip" : "outline");
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md font-medium leading-none",
        size === "sm" ? "h-[22px] px-1.5 text-[11px]" : "h-6 px-2 text-xs",
        v === "chip" && tone.chip,
        v === "outline" && cn("border border-border bg-card", tone.text),
        v === "text" && cn("px-0", tone.text),
        className,
      )}
      {...props}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", tone.dot)} aria-hidden="true" />
      {label}
    </span>
  );
};

/** Generisk chip for små meldingar (t.d. «2 blokkeringar», «Estimat»). */
export const Chip = ({ tone = "neutral", icon: Icon, outline = false, children, className, ...props }) => (
  <span
    className={cn(
      "inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-md px-1.5 text-[11px] font-medium leading-none",
      outline ? cn("border border-border bg-card", TONE[tone].text) : TONE[tone].chip,
      className,
    )}
    {...props}
  >
    {Icon ? <Icon className="h-3 w-3" /> : null}
    {children}
  </span>
);
